/**
 * Which queue a flagged payment request belongs to.
 *
 * A session with a pending request is either owed a **partial payment** — it was
 * cancelled or rescheduled with payment, so the participant is owed for the slot
 * they lost — or has been flagged for a **bonus** by a Moderator/Scheduler. The
 * two are separate queues everywhere they surface (the Action Center's two cards,
 * Payment Approvals' two filter pills), so the rule that tells them apart lives
 * here once rather than being re-derived per screen.
 *
 * Pure and component-free; the row shape is structural so this module stays
 * decoupled from the API layer.
 */

/** `schedules.cancellation_type` of a cancellation that owes a partial payment. */
export const CANCEL_WITH_PARTIAL_PAYMENT = 'With Partial Payment';

/** `schedules.reschedule_type` of a reschedule that owes a partial payment. */
export const RESCHEDULE_WITH_PAYMENT = 'With Payment';

export type PaymentRequestSource = 'cancellation' | 'reschedule' | 'bonus';

/** The two session fields the rule reads. A full schedule row satisfies this. */
export interface PaymentRequestSourceRow {
  cancellation_type?: unknown;
  reschedule_type?: unknown;
}

/**
 * Read off the session's own type columns rather than the flagged subcategory,
 * so a session that also carries a bonus flag is still counted as the payment it
 * owes. A session cancelled after being rescheduled resolves as the cancellation:
 * that is the decision that ended it.
 */
export const resolvePaymentRequestSource = (
  row: PaymentRequestSourceRow
): PaymentRequestSource => {
  if (row.cancellation_type === CANCEL_WITH_PARTIAL_PAYMENT) return 'cancellation';
  if (row.reschedule_type === RESCHEDULE_WITH_PAYMENT) return 'reschedule';
  return 'bonus';
};

/** True when the request is a partial payment rather than a bonus. */
export const isPartialPaymentRequest = (row: PaymentRequestSourceRow): boolean =>
  resolvePaymentRequestSource(row) !== 'bonus';

/**
 * Badge wording that tells a cancellation payment from a reschedule payment. A
 * bonus request needs none — its own card and filter already say so.
 */
export const PAYMENT_REQUEST_SOURCE_LABEL: Record<
  PaymentRequestSource,
  string | null
> = {
  cancellation: 'Cancellation',
  reschedule: 'Reschedule',
  bonus: null,
};

/** What the request is called where one label has to cover the whole row. */
export const PAYMENT_REQUEST_KIND_LABEL: Record<PaymentRequestSource, string> = {
  cancellation: 'Partial payment',
  reschedule: 'Partial payment',
  bonus: 'Bonus',
};
